import { useState, useEffect, useCallback } from 'react';
import { useTheme } from './themeControl/ThemeProvider';
import { useAlert } from '../providers/AlertProvider';
import { ActivityIndicator } from './custom/ActivityIndicator';
import { fetchMyTraining, fetchTrainingModuleForWeb, fetchTrainingContentUrl, submitTrainingAttemptWeb } from '../api/myTraining';

const STATUS = {
    passed:           { icon: '🟢', label: 'Passed',            bg: '#e8f5e9', border: '#388e3c', text: '#1b5e20' },
    renewal_required: { icon: '🟡', label: 'Renewal Required',  bg: '#fff3e0', border: '#FF8C00', text: '#e65100' },
    expired:          { icon: '🔴', label: 'Expired',           bg: '#ffebee', border: '#d32f2f', text: '#b71c1c' },
    not_started:      { icon: '⚫', label: 'Not Started',        bg: '#f5f5f5', border: '#585858', text: '#757575' },
    valid:            { icon: '🟢', label: 'Passed',            bg: '#e8f5e9', border: '#388e3c', text: '#1b5e20' },
};

const statusOf = (m) => {
    const s = m.record?.status ?? 'not_started';
    return s === 'valid' ? 'passed' : s;
};

export default function MyTrainingPage() {
    const { theme } = useTheme();
    const { showAlert } = useAlert();
    const isDark = theme === 'dark';
    const bg   = isDark ? '#121212' : '#f0f0f5';
    const card = isDark ? '#1e1e1e' : '#ffffff';
    const cardListBg = isDark ? '#252525' : '#f0f0f5';
    const text = isDark ? '#e0e0e0' : '#2c2c2c';
    const sub  = isDark ? '#888' : '#666';
    const border = isDark ? '#333' : '#ddd';

    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);

    // Active module modal
    const [activeModule, setActiveModule] = useState(null);   // full module detail
    const [activeOverview, setActiveOverview] = useState(null); // from overview list
    const [moduleLoading, setModuleLoading] = useState(false);
    const [contentUrl, setContentUrl] = useState(null);

    // Quiz state
    const [phase, setPhase] = useState('detail'); // 'detail' | 'quiz' | 'result'
    const [currentQ, setCurrentQ] = useState(0);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await fetchMyTraining();
        setLoading(false);
        if (res.training) setModules(res.training);
        else showAlert({ type: 'danger', message: res.error || 'Failed to load training' });
    }, [showAlert]);

    useEffect(() => { load(); }, [load]);

    const openModule = async (m) => {
        setActiveOverview(m);
        setPhase('detail');
        setAnswers({});
        setCurrentQ(0);
        setResult(null);
        setContentUrl(null);
        setModuleLoading(true);
        const [modRes, urlRes] = await Promise.all([
            fetchTrainingModuleForWeb(m.id),
            fetchTrainingContentUrl(m.id).catch(() => null),
        ]);
        setModuleLoading(false);
        if (modRes.error) { showAlert({ type: 'danger', message: modRes.error }); return; }
        setActiveModule(modRes);
        if (urlRes && !urlRes.error) setContentUrl(urlRes);
    };

    const closeModal = () => {
        setActiveModule(null);
        setActiveOverview(null);
        setContentUrl(null);
        setPhase('detail');
    };

    const handleSubmit = async () => {
        const questions = activeModule.questions || [];
        const payload = questions.map(q => ({ question_id: q.id, answer_id: answers[q.id] || '' }));
        const unanswered = payload.filter(a => !a.answer_id).length;
        if (unanswered > 0) { showAlert({ type: 'warning', message: `Please answer all ${questions.length} questions` }); return; }
        setSubmitting(true);
        const res = await submitTrainingAttemptWeb(activeModule.id, payload);
        setSubmitting(false);
        if (res.error) { showAlert({ type: 'danger', message: res.error }); return; }
        setResult(res);
        setPhase('result');
        if (res.passed) load(); // refresh list so status updates
    };

    const outstanding = modules.filter(m => { const s = statusOf(m); return s !== 'passed' && s !== 'valid'; }).length;
    const sorted = [...modules].sort((a, b) => {
        const order = ['expired', 'renewal_required', 'not_started', 'passed'];
        return order.indexOf(statusOf(a)) - order.indexOf(statusOf(b));
    });

    const cardStyle = { background: cardListBg, border: `1px solid ${border}`, borderRadius: 10, padding: '16px 20px', marginBottom: 10 };
    const btnStyle = (colour) => ({ background: colour, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 15 });

    if (loading) return (
        <div style={{ minHeight: '100vh', backgroundColor: bg }}>
            <div style={{ height: '130px' }} />
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '40px' }}>
                <ActivityIndicator />
            </div>
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', backgroundColor: bg }}>
            <div style={{ height: '130px' }} />
            <div style={{ maxWidth: 720, margin: '0 auto', paddingLeft: '16px', paddingRight: '16px', paddingBottom: '40px' }}>
                <h3 style={{ color: text, marginBottom: 4 }}>My Training</h3>
                <p style={{ color: sub, marginBottom: 24, fontSize: 14 }}>Complete your required training modules below.</p>

                {/* Summary banner */}
                {modules.length > 0 && (
                    <div style={{ background: outstanding > 0 ? '#fff3e0' : '#e8f5e9', border: `1px solid ${outstanding > 0 ? '#FF8C00' : '#388e3c'}`, borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 20 }}>{outstanding > 0 ? '🟡' : '🟢'}</span>
                        <span style={{ fontWeight: 600, color: outstanding > 0 ? '#e65100' : '#1b5e20', fontSize: 14 }}>
                            {outstanding > 0 ? `${outstanding} module${outstanding > 1 ? 's' : ''} require your attention` : 'All training is up to date'}
                        </span>
                    </div>
                )}

                {modules.length === 0 ? (
                    <div style={{ textAlign: 'center', color: sub, marginTop: 60 }}>
                        <div style={{ fontSize: 48, marginBottom: 12 }}>🎓</div>
                        <p style={{ fontSize: 16 }}>No training has been assigned to your account yet.</p>
                    </div>
                ) : sorted.map(m => {
                    const st = statusOf(m);
                    const cfg = STATUS[st] || STATUS.not_started;
                    return (
                        <div key={m.id} style={{ ...cardStyle, cursor: 'pointer', backgroundColor: isDark ? '#444444' : '#c5c4c4a4', borderColor: outstanding ? '#FF8C00' : '#388e3c', border: '2px solid' }} onClick={() => openModule(m)}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                                    <span style={{ fontSize: 22 }}>{cfg.icon}</span>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 15, color: text }}>{m.title}</div>
                                        <div style={{ fontSize: 12, color: sub }}>
                                            {cfg.label}
                                            {m.record?.score != null ? ` · ${m.record.score}%` : ''}
                                            {m.duration_minutes ? ` · ~${m.duration_minutes} min` : ''}
                                            {m.expiry_months ? ` · Renews every ${m.expiry_months} months` : ''}
                                        </div>
                                        {m.record?.expiry_date && st !== 'not_started' && (
                                            <div style={{ fontSize: 11, color: st === 'expired' ? '#d32f2f' : '#FF8C00', marginTop: 2 }}>
                                                {st === 'expired' ? 'Expired: ' : 'Expires: '}{new Date(m.record.expiry_date).toLocaleDateString('en-GB')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <span style={{ color: sub, fontSize: 18 }}>›</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Module modal */}
            {activeOverview && (
                <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', padding: 28, position: 'relative' }}>
                        <button onClick={closeModal} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: sub, zIndex: 1 }}>✕</button>

                        {moduleLoading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><ActivityIndicator /></div>
                        ) : phase === 'result' && result ? (
                            /* ── Result ── */
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                <div style={{ fontSize: 64, marginBottom: 12 }}>{result.passed ? '🟢' : '🔴'}</div>
                                <h4 style={{ color: text }}>{result.passed ? 'Training Passed!' : 'Not Passed'}</h4>
                                <div style={{ fontSize: 48, fontWeight: 900, color: result.passed ? '#388e3c' : '#d32f2f', margin: '8px 0' }}>{result.score}%</div>
                                <div style={{ color: sub, marginBottom: 20, fontSize: 14 }}>Pass mark: {result.pass_mark}% · Attempt #{result.attempt_number}</div>
                                {result.passed && <div style={{ color: '#388e3c', fontWeight: 600, marginBottom: 20 }}>Your completion has been recorded.</div>}
                                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    {!result.passed && <button style={btnStyle('#1997eb')} onClick={() => { setAnswers({}); setCurrentQ(0); setPhase('quiz'); }}>Try Again</button>}
                                    <button style={{ ...btnStyle('#888') }} onClick={closeModal}>Close</button>
                                </div>
                            </div>
                        ) : phase === 'quiz' && activeModule ? (
                            /* ── Quiz ── */
                            (() => {
                                const questions = activeModule.questions || [];
                                const q = questions[currentQ];
                                const selected = answers[q?.id];
                                const isLast = currentQ === questions.length - 1;
                                return (
                                    <div style={{ paddingRight: 32 }}>
                                        <div style={{ marginBottom: 20 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, color: sub }}>
                                                <span>{activeModule.title}</span>
                                                <span>Q{currentQ + 1} of {questions.length}</span>
                                            </div>
                                            <div style={{ height: 6, background: border, borderRadius: 3, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${((currentQ + 1) / questions.length) * 100}%`, background: '#1997eb', borderRadius: 3 }} />
                                            </div>
                                        </div>
                                        <p style={{ fontSize: 16, fontWeight: 600, color: text, marginBottom: 20, lineHeight: 1.5 }}>{q?.question}</p>
                                        {(q?.answers || []).map(a => (
                                            <div key={a.id} onClick={() => setAnswers(p => ({ ...p, [q.id]: a.id }))}
                                                style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 10, cursor: 'pointer', border: `2px solid ${selected === a.id ? '#1997eb' : border}`, background: selected === a.id ? (isDark ? '#1a2a3a' : '#e8f4fd') : card, color: text }}>
                                                <span style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${selected === a.id ? '#1997eb' : '#999'}`, display: 'inline-block', marginRight: 10, verticalAlign: 'middle', background: selected === a.id ? '#1997eb' : 'transparent' }} />
                                                {a.answer}
                                            </div>
                                        ))}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                                            <button style={{ ...btnStyle('#888'), opacity: currentQ === 0 ? 0.3 : 1 }} disabled={currentQ === 0} onClick={() => setCurrentQ(p => p - 1)}>← Previous</button>
                                            {isLast
                                                ? <button style={btnStyle(submitting ? '#aaa' : '#388e3c')} disabled={submitting || !selected} onClick={handleSubmit}>{submitting ? 'Submitting…' : 'Submit Answers'}</button>
                                                : <button style={{ ...btnStyle(selected ? '#1997eb' : '#aaa') }} disabled={!selected} onClick={() => setCurrentQ(p => p + 1)}>Next →</button>
                                            }
                                        </div>
                                    </div>
                                );
                            })()
                        ) : activeModule ? (
                            /* ── Module detail ── */
                            <div>
                                {(() => {
                                    const st = statusOf(activeOverview);
                                    const cfg = STATUS[st] || STATUS.not_started;
                                    const priorScore = activeOverview.record?.score;
                                    const expiryDate = activeOverview.record?.expiry_date;
                                    return (
                                        <>
                                            <h5 style={{ color: text, marginBottom: 6 }}>{activeModule.title}</h5>
                                            {activeModule.description && <p style={{ color: sub, fontSize: 14, marginBottom: 12 }}>{activeModule.description}</p>}
                                            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16, fontSize: 13, color: sub }}>
                                                <span>✅ Pass mark: <strong>{activeModule.pass_mark}%</strong></span>
                                                {activeModule.duration_minutes && <span>⏱ ~{activeModule.duration_minutes} min</span>}
                                                {activeModule.expiry_months && <span>🔄 Renews every {activeModule.expiry_months} months</span>}
                                            </div>

                                            {/* Prior record banner */}
                                            {st !== 'not_started' && (
                                                <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span>{cfg.icon}</span>
                                                    <span style={{ color: cfg.text, fontSize: 13, fontWeight: 600 }}>
                                                        {st === 'expired' ? 'Expired — must be retaken' : st === 'renewal_required' ? 'Renewal required' : `Last passed: ${priorScore}%`}
                                                        {expiryDate ? `  ·  ${new Date(expiryDate).toLocaleDateString('en-GB')}` : ''}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Content link */}
                                            {contentUrl?.url && (
                                                <a href={contentUrl.url} target="_blank" rel="noreferrer"
                                                    style={{ display: 'flex', alignItems: 'center', gap: 10, background: isDark ? '#2a2a2a' : '#f8f9fa', border: `1px solid ${border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 16, textDecoration: 'none', color: text }}>
                                                    <span style={{ fontSize: 22 }}>{contentUrl.type === 'video' ? '🎬' : '📄'}</span>
                                                    <span style={{ flex: 1, fontWeight: 600, fontSize: 15 }}>{contentUrl.type === 'video' ? 'Watch Training Video' : 'Open Training Document'}</span>
                                                    <span style={{ color: '#1997eb', fontSize: 13 }}>Open ↗</span>
                                                </a>
                                            )}

                                            {/* Start test */}
                                            {(activeModule.questions || []).length > 0 ? (() => {
                                                const expiringWithin30Days = expiryDate && (new Date(expiryDate) - new Date()) < 30 * 24 * 60 * 60 * 1000;
                                                const needsAction = st === 'not_started' || st === 'expired' || st === 'renewal_required' || expiringWithin30Days;
                                                const btnColor = !needsAction ? '#9e9e9e' : st === 'expired' ? '#d32f2f' : '#1997eb';
                                                const label = st === 'not_started' ? `Start Test (${activeModule.questions.length} questions)` :
                                                              st === 'expired'     ? `Retake Required (${activeModule.questions.length} questions)` :
                                                              expiringWithin30Days ? `Retake Soon — Expiring (${activeModule.questions.length} questions)` :
                                                                                     `Retake Test (${activeModule.questions.length} questions)`;
                                                return (
                                                    <button
                                                        style={{ ...btnStyle(btnColor), width: '100%', padding: '14px', fontSize: 16, cursor: needsAction ? 'pointer' : 'not-allowed', opacity: needsAction ? 1 : 0.55 }}
                                                        disabled={!needsAction}
                                                        title={!needsAction ? 'This module is completed and not due for renewal yet' : undefined}
                                                        onClick={() => { if (needsAction) { setAnswers({}); setCurrentQ(0); setPhase('quiz'); } }}>
                                                        {label}
                                                    </button>
                                                );
                                            })() : (
                                                <div style={{ color: sub, fontSize: 13, textAlign: 'center', padding: 12 }}>No test questions have been added to this module yet.</div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
}
